import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { User, League, UserContextState } from '@/types/user';
import { useAuth } from './AuthContext';

const UserContext = createContext<UserContextState | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const { isAuthenticated, authToken, user: authUser, isLoading: authLoading } = useAuth();
  const [user, setUser] = useState<User | null>(null);
  const [leagues, setLeagues] = useState<League[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Logout - clears user-specific data
   * Note: Authentication state is managed by AuthContext
   */
  const logout = useCallback(() => {
    setUser(null);
    setLeagues([]);
    setError(null);
  }, []);

  /**
   * Update user data
   */
  const updateUser = useCallback(
    (data: Partial<User>) => {
      if (!user) return;

      const updatedUser = { ...user, ...data };
      setUser(updatedUser);
    },
    [user]
  );

  /**
   * Refresh user data from API
   */
  const refreshUser = useCallback(async () => {
    if (!authToken) {
      throw new Error('No authentication token');
    }

    setIsLoading(true);
    try {
      // In production, fetch fresh user data from API
      // For now, just return current user
      await new Promise(resolve => setTimeout(resolve, 300));

      // Mock refresh - in production this would fetch from API
      if (user) {
        setUser({ ...user, updatedAt: new Date() });
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to refresh user';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [authToken, user]);

  /**
   * Join a league with invite code
   */
  const joinLeague = useCallback(
    async (inviteCode: string) => {
      if (!user) {
        throw new Error('Must be logged in to join a league');
      }

      setIsLoading(true);
      try {
        // Mock API call
        await new Promise(resolve => setTimeout(resolve, 500));

        // Mock response - add new league
        const newLeague: League = {
          id: `league-${Date.now()}`,
          name: 'New League',
          color: Math.random() > 0.5 ? 'orange' : 'blue',
          memberCount: 5,
          members: [],
          createdBy: user?.id ?? '',
          isOwner: false,
          inviteCode,
          createdAt: new Date(),
        };

        setLeagues(prev => [...prev, newLeague]);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to join league';
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [user]
  );

  /**
   * Create a new league
   */
  const createLeague = useCallback(
    async (name: string, color: 'orange' | 'blue'): Promise<League> => {
      if (!user) {
        throw new Error('Must be logged in to create a league');
      }

      setIsLoading(true);
      try {
        // Mock API call
        await new Promise(resolve => setTimeout(resolve, 500));

        // Generate invite code
        const inviteCode = Math.random().toString(36).substring(2, 8).toUpperCase();

        // Mock response - create new league
        const newLeague: League = {
          id: `league-${Date.now()}`,
          name,
          color,
          memberCount: 1,
          members: [],
          createdBy: user.id,
          isOwner: true,
          inviteCode,
          createdAt: new Date(),
        };

        setLeagues(prev => [...prev, newLeague]);
        return newLeague;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to create league';
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [user]
  );

  /**
   * Sync user data from AuthContext once authentication is complete
   */
  useEffect(() => {
    console.log('[UserContext] 🔄 Sync effect triggered');
    console.log('[UserContext] 📍 authLoading:', authLoading);
    console.log('[UserContext] 🔐 isAuthenticated:', isAuthenticated);
    console.log('[UserContext] 👤 authUser:', authUser);
    console.log('[UserContext] 📍 authUser.location:', authUser?.location);

    // Wait for AuthContext to complete initialization
    if (authLoading) {
      console.log('[UserContext] ⏳ Still loading, waiting...');
      setIsLoading(true);
      return;
    }

    // If authenticated, sync user data from AuthContext
    if (isAuthenticated && authUser) {
      console.log('[UserContext] ✅ Authenticated, syncing user data');
      console.log('[UserContext] 👤 Setting user to:', authUser);
      console.log('[UserContext] 📍 User location being set:', authUser.location);
      setUser(authUser);

      // Load mock leagues (in production, fetch from API)
      setLeagues([
        {
          id: 'league1',
          name: 'Friends League',
          color: 'orange',
          memberCount: 8,
          members: [],
          createdBy: authUser.id,
          isOwner: true,
        },
        {
          id: 'league2',
          name: 'Work League',
          color: 'blue',
          memberCount: 12,
          members: [],
          createdBy: authUser.id,
          isOwner: false,
        },
      ]);
    } else {
      // Not authenticated, clear user data
      console.log('[UserContext] ❌ Not authenticated or no authUser, clearing data');
      console.log('[UserContext] 🔐 isAuthenticated:', isAuthenticated);
      console.log('[UserContext] 👤 authUser:', authUser);
      setUser(null);
      setLeagues([]);
    }

    console.log('[UserContext] ✅ Sync complete, setting isLoading to false');
    setIsLoading(false);
  }, [isAuthenticated, authUser, authLoading]);

  const value: UserContextState = {
    user,
    leagues,
    isAuthenticated,
    isLoading,
    error,
    token: authToken,
    logout,
    updateUser,
    refreshUser,
    joinLeague,
    createLeague,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

/**
 * Hook to use UserContext
 */
export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
